import React, {Fragment, useState} from "react";
import { connect } from "react-redux";
import {FormattedMessage, injectIntl} from "react-intl";
import {withRouter} from "react-router-dom";
import trim from "lodash/trim";
import PropTypes from "prop-types";

import PhoneIcon from '@mui/icons-material/Phone';
import TextField from "@mui/material/TextField";
import CheckIcon from '@mui/icons-material/Check';

import {setUserPopupContent, setUserTwoFa} from "../../../actions/userFunctions";
import {LOOK_OVERLAYS} from "../core/constants";
import {IS_PRODUCT_JOURNEY} from "../../../config";
import Analytics, {LOOK_JOURNEY_ANALYTIC_EVENTS, PRODUCT_JOURNEY_ANALYTIC_EVENTS} from "../../../analytics";
import {UserPopupContent} from "../../visComponents/constants";
import {ROUTES} from "../../../routes";
import {
  generateTwoFaAuthenticationToken,
  sendAuthenticationRequest,
  validateTwoFaAuthentication
} from "../../../api/user-api";
import MaterialSpinner from "../../ui/materialSpinner";
import {changeUserInfo, setUserPhoneNumberValidated} from "../../../actions/actionFunctions";
import {setLookOverlayOpened} from "../../../actions/lookFunctions";
import {setLookAiImageOpened} from "../../ai/aiActions";

export const sendAuthRequest = async (twoFALoading, analyticsLabel, userInfo, userIsLoggedIn, type, setUserTwoFa) => {
  if (twoFALoading) {
    return false
  }
  setUserTwoFa({
    twoFALoading: true
  });
  Analytics.sendClickEvent(analyticsLabel);
  const twoFAToken = await generateTwoFaAuthenticationToken();
  if (twoFAToken && userIsLoggedIn) {
    setUserTwoFa({ // set token to redux store, so the input can check it
      twoFAToken
    });
    await sendAuthenticationRequest(twoFAToken, `+${userInfo.country.dialCode} ${userInfo.phoneNumber}`, type);
  }
  setUserTwoFa({
    twoFALoading: false
  });
}

const TwoFaCodeInput = (props) => {

  const {history, intl, setUserPhoneNumberValidated, userInfo, isPhoneNumberValidated, lookOverlayOpened,
    setUserPopupContent, userTwoFa, setUserTwoFa, closePopup, location, lookOverlayAction, setLookOverlayOpened,
    setLookAiImageOpened} = props;
  const {twoFAToken, twoFALoading} = userTwoFa;

  const [authCode, setAuthCode] = useState("");
  const [inValidCode, setInValidCode] = useState("");
  const [isBlurred, setIsBlurred] = useState(false);

  const updatePhoneButtonClick = () => {
    if (twoFALoading) {
      return false
    }
    if (lookOverlayOpened === LOOK_OVERLAYS.leadCapture) {
      const analyticsLabel = IS_PRODUCT_JOURNEY
        ? PRODUCT_JOURNEY_ANALYTIC_EVENTS.leadCapturePopupTwoFAUpdatePhone
        : LOOK_JOURNEY_ANALYTIC_EVENTS.lookLeadCapturePopupTwoFAUpdatePhone;
      Analytics.sendClickEvent(analyticsLabel);
      setUserPopupContent(UserPopupContent.twoFA);
      return false
    }
    Analytics.sendClickEvent(PRODUCT_JOURNEY_ANALYTIC_EVENTS.cartTwoFAUpdatePhone);
    history.push(ROUTES.cartTwoFA);
  };

  const checkCode = async (newAuthCode) => {
    setUserTwoFa({
      twoFALoading: true
    });
    const validated = await validateTwoFaAuthentication(twoFAToken, newAuthCode, userInfo.phoneNumber);
    console.log("validated", validated);
    setUserTwoFa({
      twoFALoading: false
    });
    Analytics.sendEvent("PhoneVerification", validated ? "Success" : "Failure");
    if (validated) {
      setUserPhoneNumberValidated(true);
      const currentLocation = location.pathname;
      history.push(currentLocation + "/phone-verified-thankyou");
      setTimeout(() => {
        if (lookOverlayOpened === LOOK_OVERLAYS.leadCapture && closePopup) {
          history.push(currentLocation);
          const fromAI = lookOverlayAction === "aiLeadCapture";
          if (fromAI) {
            setLookOverlayOpened(LOOK_OVERLAYS.aiPanel);
            setLookAiImageOpened(true);
          }
          closePopup();
        } else {
          history.push(ROUTES.cartTerms);
        }
      }, 2000);
    } else {
      setInValidCode(intl.formatMessage({id: "look.inputs.errors.incorrectCode"}));
    }
  }

  const onUserInputChange = (e) => {
    const newAuthCode = trim(e.target.value).replace(/[^0-9]/g, "");
    setAuthCode(newAuthCode);

    if (newAuthCode.length === 6) {
      if (inValidCode) {
        setInValidCode();
      }
      checkCode(newAuthCode);
    } else if (newAuthCode.length > 6) {
      setInValidCode(intl.formatMessage({id: "look.inputs.errors.maxCode"}));
    } else if (isBlurred && newAuthCode.length < 6) {
      setInValidCode(intl.formatMessage({id: "look.inputs.errors.minCode"}));
    } else if (inValidCode) {
      setInValidCode("");
    }
  }

  return (
    <div className="gc-form__center">
      <div className="gc-form__inputs-block">
        <div className="look-block">
          <TextField className="look-input"
                     type={"text"}
                     value={authCode}
                     required={true}
                     size="small"
                     label={intl.formatMessage({id: "look.inputs.twoFA"})}
                     onChange={onUserInputChange}
                     onBlur={() => setIsBlurred(true)}
                     error={!!inValidCode}
                     helperText={inValidCode}
                     variant="outlined" />
        </div>
      </div>
      <div className="gc-link bold" onClick={isPhoneNumberValidated ? null : updatePhoneButtonClick}>
        {isPhoneNumberValidated ? (
          <Fragment>
            <CheckIcon/> <FormattedMessage id="grohe.cart.twoFA.verify.updatePhoneSuccess"/>
          </Fragment>
        ) : twoFALoading ? <MaterialSpinner size={28}/> : (
          <Fragment>
            <span className="gc-link__icon"><PhoneIcon/></span> <FormattedMessage id="grohe.cart.twoFA.verify.updatePhone"/>
          </Fragment>
        )}
      </div>
    </div>
  )
};

TwoFaCodeInput.propTypes = {
  closePopup: PropTypes.func
}

const mapStateToProps = (store) => {
  const {userInfo, isPhoneNumberValidated, userIsLoggedIn, userTwoFa} = store.userState;
  const {lookOverlayOpened, lookOverlayAction} = store.lookState;

  return {
    lookOverlayOpened,
    userInfo,
    isPhoneNumberValidated,
    userIsLoggedIn,
    userTwoFa,
    lookOverlayAction,
  };
};

const mapDispatchToProps = {
  changeUserInfo,
  setUserPhoneNumberValidated,
  setUserTwoFa,
  setUserPopupContent,
  setLookOverlayOpened,
  setLookAiImageOpened,
};

export default injectIntl(
  withRouter(
    connect(mapStateToProps, mapDispatchToProps)(TwoFaCodeInput)
  )
)
